
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    
    if (formData.message.length > 500) {
      setSubmitStatus('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('subject', formData.subject);
      form.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4td5qq2t66nrlpqes3g', {
        method: 'POST',
        body: new URLSearchParams(form as any)
      });

      if (response.ok) {
        setSubmitStatus('Message sent successfully! We\'ll get back to you soon.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="w-2 h-2 bg-[#00D9FF] rounded-full mr-3"></div>
              <span className="text-[#00D9FF] font-semibold uppercase tracking-wider text-sm">GET IN TOUCH</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] mb-8 leading-tight">
              We're Here<br />
              <span className="text-[#00D9FF]">To Help</span>
            </h2>

            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#00D9FF] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1628] mb-2">Email</h3>
                  <a 
                    href="mailto:support@eltromsrl.com" 
                    className="text-[#6B7B93] hover:text-[#00D9FF] transition-colors duration-200"
                  >
                    support@eltromsrl.com
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#00D9FF] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1628] mb-2">Phone</h3>
                  <a 
                    href="tel:+17752547012" 
                    className="text-[#6B7B93] hover:text-[#00D9FF] transition-colors duration-200"
                  >
                    +1 775-254-7012
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#00D9FF] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#0A1628] mb-2">Address</h3>
                  <div className="text-[#6B7B93] leading-relaxed">
                    Santa Fe 413/7<br />
                    Mendoza, M5500DBG<br />
                    Argentina
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#0A1628] mb-8">Send us a Message</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder=" "
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-[#00D9FF] peer"
                    required
                    disabled={isSubmitting}
                  />
                  <label className="absolute left-4 top-4 text-[#6B7B93] transition-all duration-200 peer-focus:text-xs peer-focus:-translate-y-2 peer-focus:text-[#00D9FF] peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-2">
                    Full Name
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder=" "
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-[#00D9FF] peer"
                    required
                    disabled={isSubmitting}
                  />
                  <label className="absolute left-4 top-4 text-[#6B7B93] transition-all duration-200 peer-focus:text-xs peer-focus:-translate-y-2 peer-focus:text-[#00D9FF] peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-2">
                    Email Address
                  </label>
                </div>
              </div>

              <div className="relative mb-6">
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder=" "
                  className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-[#00D9FF] peer"
                  disabled={isSubmitting}
                />
                <label className="absolute left-4 top-4 text-[#6B7B93] transition-all duration-200 peer-focus:text-xs peer-focus:-translate-y-2 peer-focus:text-[#00D9FF] peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-2">
                  Subject (Optional)
                </label>
              </div>

              <div className="relative mb-6">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder=" "
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-[#00D9FF] peer resize-none"
                  required
                  disabled={isSubmitting}
                />
                <label className="absolute left-4 top-4 text-[#6B7B93] transition-all duration-200 peer-focus:text-xs peer-focus:-translate-y-2 peer-focus:text-[#00D9FF] peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:-translate-y-2">
                  Your Message
                </label>
                <div className="text-right text-sm text-[#6B7B93] mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-xl text-sm ${
                  submitStatus.includes('successfully') 
                    ? 'bg-green-50 text-green-600 border border-green-200' 
                    : 'bg-red-50 text-red-600 border border-red-200'
                }`}>
                  {submitStatus}
                </div>
              )}

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="lg"
                  disabled={isSubmitting}
                  className="min-w-32"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-line animate-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line ml-2"></i>
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
